import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilterName,
  LocationListingProps,
  LocationProps,
  View
} from './types/locationListingTypes';
import { v4 as uuidv4 } from 'uuid';
import MultiSelectDropdown, { Option } from '../../components/react/MultiSelect';
import classnames from 'classnames';
import { debounce } from '../../helpers';
import LocationItem from './LocationItem';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Library } from '@googlemaps/js-api-loader';
import ExitIntentModal from '../exit-intent-modal';

const libraries: Library[] = ['places'];
const zoomLevel = 11;

const LocationListing = ({
  googleMapsApiKey,
  locationApiUrl,
  defaultLatitude,
  defaultLongitude,
  labelSearchInput,
  labelLocateBtn,
  labelSearchBtn,
  labelBranchCheckbox,
  labelAtmCheckbox,
  labelCoopCheckbox,
  labelListViewBtn,
  labelMapViewBtn,
  filterPlaceholder,
  filterOptions,
  filterApplyLabel,
  filterResetLabel,
  ariaLabelCloseBtn,
  labelMilesAway,
  labelGetDirectionsBtn,
  labelCallUsBtn,
  labelGetDirectionsModalTitle,
  labelGetDirectionsModalMessage,
  labelGetDirectionsModalProceedBtn,
  labelLearnMoreBtn,
  labelBackToTopBtn,
  mapIframeTitle
}: LocationListingProps) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: googleMapsApiKey,
    libraries
  });

  const [view, setView] = useState(View.LIST);
  const [filters, setFilters] = useState({
    [FilterName.BRANCH]: true,
    [FilterName.ATM]: false,
    [FilterName.COOP]: false
  });
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [locationValue, setLocationValue] = useState('');
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [latitude, setLatitude] = useState(defaultLatitude);
  const [longitude, setLongitude] = useState(defaultLongitude);
  const [rawLocations, setRawLocations] = useState<LocationProps[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<LocationProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inputError, setInputError] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const locationListingRef = useRef<HTMLDivElement>(null);
  const locationListingListRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const updatedFilters = { ...filters };

    if (searchParams.has(FilterName.BRANCH)) {
      updatedFilters[FilterName.BRANCH] = searchParams.get(FilterName.BRANCH) === 'true';
    }
    if (searchParams.has(FilterName.ATM)) {
      updatedFilters[FilterName.ATM] = searchParams.get(FilterName.ATM) === 'true';
    }
    if (searchParams.has(FilterName.COOP)) {
      updatedFilters[FilterName.COOP] = searchParams.get(FilterName.COOP) === 'true';
    }

    if (
      !updatedFilters[FilterName.BRANCH] &&
      !updatedFilters[FilterName.ATM] &&
      !updatedFilters[FilterName.COOP]
    ) {
      updateQueryString(FilterName.BRANCH, 'true');
      updatedFilters[FilterName.BRANCH] = true;
    }

    setFilters(updatedFilters);
   
 new ExitIntentModal(this as unknown as HTMLElement);
  
   
  }, []);

  useEffect(() => {
    handleWindowResize();

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    fetchLocations();

    if (mapRef.current) {
      mapRef.current.panTo({ lat: Number(latitude), lng: Number(longitude) });
      mapRef.current.setZoom(zoomLevel);
    }
  }, [latitude, longitude]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setTimeout(() => {
      mapRef.current?.getDiv().querySelector('iframe')?.setAttribute('title', mapIframeTitle);
    }, 1000);
  }, []);

  useEffect(() => {
    fitMapBounds();
  }, [filteredLocations]);

  useEffect(() => {
    filterLocations();
  }, [filters, rawLocations, selectedServices]);

  // Add effect to handle scrolling when selected location changes
  useEffect(() => {
    if (!selectedLocation) return;

    const locationItemElement = locationListingListRef.current?.querySelector(
      `#${selectedLocation}`
    );
    if (!locationItemElement) return;

    const element = locationItemElement as HTMLElement;
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      if (view === View.LIST) {
        const searchBar = locationListingRef.current?.querySelector(
          '.location-listing__bar-wrapper'
        );
        const filterArea = locationListingRef.current?.querySelector(
          '.location-listing__filter-area'
        );
        const listContainer = locationListingListRef.current;

        if (filterArea && listContainer && searchBar) {
          // First ensure the search bar and filters are visible at the top
          const searchBarRect = searchBar.getBoundingClientRect();
          if (searchBarRect.top < 0) {
            window.scrollTo({
              top: window.scrollY + searchBarRect.top,
              behavior: 'smooth'
            });
          }

          // Calculate the scroll position within the list container
          const elementOffsetTop = element.offsetTop;
          listContainer.scrollTop = elementOffsetTop;
        }
      } else {
        // In map view, ensure map is visible first
        const mapContainer = locationListingRef.current?.querySelector(
          '.location-listing__content-map'
        );
        if (mapContainer) {
          const mapRect = mapContainer.getBoundingClientRect();
          const mapTop = mapRect.top + window.scrollY;

          window.scrollTo({
            top: mapTop,
            behavior: 'smooth'
          });

          // Then scroll the list container to show the element
          setTimeout(() => {
            locationListingListRef.current?.scrollTo({
              top: element.offsetTop,
              behavior: 'smooth'
            });
          }, 100);
        }
      }
    } else {
      // Desktop behavior - scroll the list container
      locationListingListRef.current?.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth'
      });
    }
  }, [selectedLocation, view]);

  const handleWindowResize = () => {
    setTimeout(() => {
      const locationListingWidth = locationListingListRef.current?.offsetWidth || 0;
      const locationListingLeft = locationListingListRef.current?.offsetLeft || 0;
      const locationListingSpace = locationListingWidth + locationListingLeft;

      locationListingRef.current?.style.setProperty(
        '--location-listing-space',
        `${locationListingSpace}px`
      );
    });
  };

  const handleViewChange = (newView: View) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setView(newView);
  };

  const handleApplyFilters = (selectedItems: Option[]) => {
    const newSelectedServices = selectedItems.filter(item => item.selected).map(item => item.id);
    setSelectedServices(newSelectedServices);
  };

  const handleGetLocation = async () => {
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      position => {
        setLatitude(position.coords.latitude.toString());
        setLongitude(position.coords.longitude.toString());
        setFetchingLocation(false);
      },
      error => {
        console.error(error?.message);
        setFetchingLocation(false);
      }
    );
  };

  const handleBackListToTop = () => {
    locationListingListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCheckboxChange = (name: FilterName) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setFilters(prevState => ({
      ...prevState,
      [name]: checked
    }));
    updateQueryString(name, checked ? 'true' : 'false');
  };

  const getLocationByName = (name: string) => {
    const location = filteredLocations.find(location =>
      location.name?.toLowerCase().includes(name?.toLowerCase())
    );

    return location;
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = searchInputRef.current?.validity.valid;
    setInputError(!isValid);

    if (locationValue) {
      const existingLocation = getLocationByName(locationValue);
      const { latitude, longitude } = existingLocation?.address || {};
      const { name } = existingLocation || {};

      if (existingLocation && latitude && longitude && name) {
        setLatitude(latitude);
        setLongitude(longitude);
        setLocationValue(name);
      } else {
        getCoordinates(locationValue);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocationValue(e.target.value);
    if (e.target.value) {
      setInputError(false);
    }
  };

  const fetchLocations = async () => {
    setIsLoading(true);
    const url = `${locationApiUrl}?lat=${latitude}&lng=${longitude}`;
    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => {
        const originalLocations = data.map((location: LocationProps, index: number) => ({
          ...location,
          id: `location-${index}-${location.id}`,
          services: Array.isArray(location.services) ? location.services : []
        }));
        setRawLocations(originalLocations);
        handleBackListToTop();
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const debounceHandleListScroll = debounce((scrollTop: unknown) => {
    setShowBackToTop((scrollTop as number) > 300);
  }, 100);

  const filterLocations = () => {
    const filteredLocations = rawLocations.filter(location => {
      return Object.keys(filters).some(key => {
        if (selectedServices.length === 0) {
          return filters[key as FilterName] && location.locatorType === key;
        }

        const hasSelectedService = selectedServices.some(service =>
          location.services.includes(service)
        );
        return filters[key as FilterName] && location.locatorType === key && hasSelectedService;
      });
    });
    setFilteredLocations(filteredLocations);
    setSelectedLocation(null);
  };

  const updateQueryString = (name: FilterName, value: string) => {
    const searchParams = new URLSearchParams(window.location.search);

    if (value) {
      searchParams.set(name, value);
    } else {
      searchParams.delete(name);
    }

    window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);
  };

  const searchInputId = useMemo(() => uuidv4(), []);
  const branchInputId = useMemo(() => uuidv4(), []);
  const atmInputId = useMemo(() => uuidv4(), []);
  const coopInputId = useMemo(() => uuidv4(), []);

  const mapContainerStyle: CSSProperties = {
    width: '100%',
    height: '100%'
  };

  const initialFilterOptions = useMemo(() => {
    return JSON.parse(filterOptions).map((item: Option) => ({
      ...item,
      selected: false
    }));
  }, [filterOptions]);

  const fitMapBounds = useCallback(() => {
    if (mapRef.current && filteredLocations.length > 0) {
      mapRef.current.panTo({ lat: Number(latitude), lng: Number(longitude) });
      mapRef.current.setZoom(zoomLevel);
    }
  }, [filteredLocations, mapRef]);

  const handleOnLocationClick = (locationId: string, centerMap: boolean = true) => {
    // If clicking the same location that's already selected, deselect it
    if (selectedLocation === locationId) {
      setSelectedLocation(null);
      return;
    }

    setSelectedLocation(locationId);

    if (centerMap && mapRef.current) {
      const selectedLocation = filteredLocations.find(location => location.id === locationId);
      if (selectedLocation) {
        const { latitude, longitude } = selectedLocation.address;
        const latLng = { lat: Number(latitude), lng: Number(longitude) };

        // First ensure the map container is visible (for mobile)
        const isMobile = window.innerWidth < 768;
        if (isMobile && view === View.MAP) {
          const mapContainer = locationListingRef.current?.querySelector(
            '.location-listing__content-map'
          );
          if (mapContainer) {
            const mapRect = mapContainer.getBoundingClientRect();
            window.scrollTo({
              top: window.scrollY + mapRect.top,
              behavior: 'smooth'
            });
          }
        }

        // Center the map on the location
        mapRef.current.panTo(latLng);
      }
    }
  };

  // Determine which locations to display on the map
  const locationsToDisplay = useMemo(() => {
    // Always show all filtered locations on the map
    return filteredLocations;
  }, [filteredLocations]);

  // Determine which locations to display in the list view
  const listLocationsToDisplay = useMemo(() => {
    // Always show all filtered locations
    return filteredLocations;
  }, [filteredLocations]);

  const mapMarkerIcon = useCallback((type: string) => {
    switch (type) {
      case FilterName.BRANCH:
        return {
          url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNSIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDIzIDMzIiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAyMyAzMyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PHBhdGggZD0iTTExLjUgNnMtLjEgMCAwIDBjLTMuMSAwLTUuNiAyLjUtNS42IDUuNiAwIDMgMi41IDUuNSA1LjYgNS41aC4xYzMgMCA1LjUtMi41IDUuNS01LjZTMTQuNSA2IDExLjUgNnoiIHN0eWxlPSJmaWxsOiNmZmYiLz48cGF0aCBkPSJNMTEuNSAwQzUuMi4xLjIgNS4xIDAgMTEuNGMwIDUuNSA5LjUgMTkuNSAxMC42IDIxLjEuMi4zLjUuNS45LjVzLjctLjIuOS0uNUMxMy41IDMwLjkgMjMgMTYuOSAyMyAxMS40IDIyLjggNS4xIDE3LjguMSAxMS41IDB6bS4xIDE3cy0uMSAwIDAgMGMtMy4xIDAtNS42LTIuNC01LjctNS41IDAtMyAyLjQtNS41IDUuNS01LjZoLjFjMyAwIDUuNSAyLjQgNS42IDUuNVMxNC42IDE3IDExLjYgMTd6IiBzdHlsZT0iZmlsbDojMDA1ZWI4Ii8+PC9zdmc+'
        };
      case FilterName.ATM:
        return {
          url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNSIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDIzIDMzIiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAyMyAzMyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PHBhdGggZD0iTTExLjUgNnMtLjEgMCAwIDBjLTMuMSAwLTUuNiAyLjUtNS42IDUuNiAwIDMgMi41IDUuNSA1LjYgNS41aC4xYzMgMCA1LjUtMi41IDUuNS01LjZTMTQuNSA2IDExLjUgNnoiIHN0eWxlPSJmaWxsOiNmZmYiLz48cGF0aCBkPSJNMTEuNSAwQzUuMi4xLjIgNS4xIDAgMTEuNGMwIDUuNSA5LjUgMTkuNSAxMC42IDIxLjEuMi4zLjUuNS45LjVzLjctLjIuOS0uNUMxMy41IDMwLjkgMjMgMTYuOSAyMyAxMS40IDIyLjggNS4xIDE3LjguMSAxMS41IDB6bS4xIDE3cy0uMSAwIDAgMGMtMy4xIDAtNS42LTIuNC01LjctNS41IDAtMyAyLjQtNS41IDUuNS01LjZoLjFjMyAwIDUuNSAyLjQgNS42IDUuNVMxNC42IDE3IDExLjYgMTd6IiBzdHlsZT0iZmlsbDojZTgxYzJkIi8+PC9zdmc+'
        };
      case FilterName.COOP:
        return {
          url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNSIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDIzIDMzIiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAyMyAzMyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PHBhdGggZD0iTTExLjUgNnMtLjEgMCAwIDBjLTMuMSAwLTUuNiAyLjUtNS42IDUuNiAwIDMgMi41IDUuNSA1LjYgNS41aC4xYzMgMCA1LjUtMi41IDUuNS01LjZTMTQuNSA2IDExLjUgNnoiIHN0eWxlPSJmaWxsOiNmZmYiLz48cGF0aCBkPSJNMTEuNSAwQzUuMi4xLjIgNS4xIDAgMTEuNGMwIDUuNSA5LjUgMTkuNSAxMC42IDIxLjEuMi4zLjUuNS45LjVzLjctLjIuOS0uNUMxMy41IDMwLjkgMjMgMTYuOSAyMyAxMS40IDIyLjggNS4xIDE3LjguMSAxMS41IDB6bS4xIDE3cy0uMSAwIDAgMGMtMy4xIDAtNS42LTIuNC01LjctNS41IDAtMyAyLjQtNS41IDUuNS01LjZoLjFjMyAwIDUuNSAyLjQgNS42IDUuNVMxNC42IDE3IDExLjYgMTd6IiBzdHlsZT0iZmlsbDojMDZiZWUxIi8+PC9zdmc+'
        };
      default:
        return undefined;
    }
  }, []);

  const getCoordinates = (address: string) => {
    if (!address.trim()) return;

    setFetchingLocation(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder
      .geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const { lat, lng } = results[0].geometry.location;
          setLatitude(lat().toString());
          setLongitude(lng().toString());
        } else {
          console.error('Geocode error:', status);
        }
      })
      .finally(() => {
        setFetchingLocation(false);
      });
  };

  return (
    <div
      className="location-listing"
      ref={locationListingRef}
      role="region"
      aria-label="Location search results"
    >
      <div role="status" aria-live="polite" className="visually-hidden">
        {isLoading ? 'Loading locations, please wait' : ''}
      </div>

      <div
        className={classnames('location-listing__loader', {
          'location-listing__loader--visible': isLoading
        })}
        aria-hidden={!isLoading}
      >
        <div className="loader" aria-hidden="true"></div>
      </div>

      <div className="location-listing__bar-wrapper">
        <form className="location-listing__bar" onSubmit={handleFormSubmit} noValidate={false}>
          <div className="location-listing__bar-search">
            <div className="input">
              <input
                id={searchInputId}
                placeholder=""
                className="input__primary"
                type="text"
                required
                value={locationValue}
                onChange={handleInputChange}
                disabled={fetchingLocation}
                aria-required="true"
                ref={searchInputRef}
                onInvalid={() => setInputError(true)}
              />
              <label htmlFor={searchInputId}>{labelSearchInput}</label>
              <span className="input__bar"></span>
              <div className="input__error-message"></div>

              <div aria-live="assertive" className="visually-hidden" role="status">
                {inputError ? 'Please enter a location to search' : ''}
              </div>

              <button
                type="button"
                aria-label={labelLocateBtn}
                onClick={handleGetLocation}
                disabled={fetchingLocation}
              >
                {fetchingLocation ? (
                  <div className="loader" role="status" aria-live="polite">
                    <span className="visually-hidden">Finding your location, please wait</span>
                  </div>
                ) : (
                  <svg className="icon" aria-hidden="true" focusable="false">
                    <use xlinkHref="#icon-locate" href="#icon-locate"></use>
                  </svg>
                )}
              </button>
            </div>

            <button type="submit" className="btn btn--primary" aria-label={labelSearchBtn}>
              {labelSearchBtn}
            </button>
          </div>

          <div className="location-listing__bar-filter">
            <div className="checkbox-with-icon">
              <input
                type="checkbox"
                id={branchInputId}
                name={FilterName.BRANCH}
                value="true"
                onChange={handleCheckboxChange(FilterName.BRANCH)}
                checked={filters[FilterName.BRANCH]}
              />
              <label htmlFor={branchInputId}>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  className={`svg-location-pin--${FilterName.BRANCH}`}
                >
                  <use xlinkHref="#icon-location-pin" href="#icon-location-pin"></use>
                </svg>
                <span>{labelBranchCheckbox}</span>
              </label>
            </div>

            <div className="checkbox-with-icon">
              <input
                type="checkbox"
                id={atmInputId}
                name={FilterName.ATM}
                value="true"
                onChange={handleCheckboxChange(FilterName.ATM)}
                checked={filters[FilterName.ATM]}
              />
              <label htmlFor={atmInputId}>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  className={`svg-location-pin--${FilterName.ATM}`}
                >
                  <use xlinkHref="#icon-location-pin" href="#icon-location-pin"></use>
                </svg>
                <span>{labelAtmCheckbox}</span>
              </label>
            </div>

            <div className="checkbox-with-icon">
              <input
                type="checkbox"
                id={coopInputId}
                name={FilterName.COOP}
                value="true"
                onChange={handleCheckboxChange(FilterName.COOP)}
                checked={filters[FilterName.COOP]}
              />
              <label htmlFor={coopInputId}>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  className={`svg-location-pin--${FilterName.COOP}`}
                >
                  <use xlinkHref="#icon-location-pin" href="#icon-location-pin"></use>
                </svg>
                <span>{labelCoopCheckbox}</span>
              </label>
            </div>
          </div>
        </form>
      </div>

      <div className="location-listing__content__wrapper">
        <div className="location-listing__content">
          <div
            className={classnames('location-listing__content-view', {
              'location-listing__content-view--list': view === View.LIST,
              'location-listing__content-view--map': view === View.MAP
            })}
          >
            <div className="location-listing__filter-area">
              <div className="location-listing__filter-view">
                <button
                  type="button"
                  className={classnames('btn btn--primary btn--outline', {
                    'btn--active': view === View.LIST
                  })}
                  onClick={handleViewChange(View.LIST)}
                >
                  {labelListViewBtn}
                </button>
                <button
                  type="button"
                  className={classnames('btn btn--primary btn--outline', {
                    'btn--active': view === View.MAP
                  })}
                  onClick={handleViewChange(View.MAP)}
                >
                  {labelMapViewBtn}
                </button>
              </div>
              <div className="location-listing__filter">
                <svg aria-hidden="true" focusable="false">
                  <use xlinkHref="#icon-filter" href="#icon-filter"></use>
                </svg>
                <MultiSelectDropdown
                  placeholder={filterPlaceholder}
                  options={initialFilterOptions}
                  labelApply={filterApplyLabel}
                  labelReset={filterResetLabel}
                  handleApplyFilters={handleApplyFilters}
                  handleResetFilters={() => {}}
                />
              </div>
            </div>

            <div className="location-listing__content-map">
              {isLoaded ? (
                <GoogleMap mapContainerStyle={mapContainerStyle} onLoad={onMapLoad}>
                  {locationsToDisplay.map(loc => (
                    <Marker
                      key={loc.id}
                      position={{
                        lat: Number(loc.address.latitude),
                        lng: Number(loc.address.longitude)
                      }}
                      animation={
                        selectedLocation === loc.id ? google.maps.Animation.BOUNCE : undefined
                      }
                      onClick={
                        selectedLocation === loc.id
                          ? () => setSelectedLocation(null)
                          : () => handleOnLocationClick(loc.id, true)
                      }
                      icon={mapMarkerIcon(loc.locatorType)}
                    />
                  ))}
                </GoogleMap>
              ) : null}
            </div>

            <div
              className="location-listing__list"
              onScroll={e => debounceHandleListScroll(e.currentTarget.scrollTop)}
              ref={locationListingListRef}
              tabIndex={-1}
            >
              {listLocationsToDisplay.map(location => {
                return (
                  <LocationItem
                    key={location.id}
                    locationId={location.id}
                    type={FilterName.BRANCH}
                    isActive={selectedLocation === location.id}
                    onClick={id => handleOnLocationClick(id, true)}
                    ariaLabelCloseBtn={ariaLabelCloseBtn}
                    labelMilesAway={labelMilesAway}
                    labelGetDirectionsBtn={labelGetDirectionsBtn}
                    labelCallUsBtn={labelCallUsBtn}
                    labelGetDirectionsModalTitle={labelGetDirectionsModalTitle}
                    labelGetDirectionsModalMessage={labelGetDirectionsModalMessage}
                    labelGetDirectionsModalProceedBtn={labelGetDirectionsModalProceedBtn}
                    labelLearnMoreBtn={labelLearnMoreBtn}
                    locationData={location}
                  />
                );
              })}
            </div>

            <div
              className={classnames('location-listing__back-to-top', {
                'location-listing__back-to-top--show': showBackToTop
              })}
            >
              <button
                type="button"
                className="btn btn--primary btn--outline"
                onClick={handleBackListToTop}
              >
                <svg className="icon" aria-hidden="true" focusable="false">
                  <use xlinkHref="#icon-chevron-up" href="#icon-chevron-up"></use>
                </svg>
                {labelBackToTopBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationListing;
